/**
 * linkedOrder.ts — a link straight to one order in the patient's chart.
 *
 * `/patients/:id?section=…&order=<integrationOrderId>&orderVendor=<vendorKey>`
 * opens the chart on that section, on the sub-tab holding that vendor's orders
 * (Labs → SiPhox, Peptides → Vial Orders, …), with the order's details open.
 * The Marketplace dashboard links here.
 *
 * The vendor key travels with the id because each orders table lists one
 * vendor: it says which table opens the order, without loading every table.
 * PatientDetailPage reads the link and passes it down as props (the tabs are
 * rendered without a router in their tests); the table that opens the order
 * calls back so the page can take the link out of the URL.
 */
export const ORDER_PARAM = 'order';
export const ORDER_VENDOR_PARAM = 'orderVendor';

export interface LinkedOrder {
  id: number;
  vendorKey: string;
}

export function parseLinkedOrder(params: URLSearchParams): LinkedOrder | null {
  const id = Number(params.get(ORDER_PARAM));
  const vendorKey = params.get(ORDER_VENDOR_PARAM);
  return Number.isInteger(id) && id > 0 && vendorKey ? { id, vendorKey } : null;
}

/** The query string for one order, to append to a chart link. */
export function linkedOrderSearch(order: LinkedOrder): string {
  return `${ORDER_PARAM}=${order.id}&${ORDER_VENDOR_PARAM}=${encodeURIComponent(order.vendorKey)}`;
}

/** The order id for the table listing `integrationKey`'s orders, if the link is for it. */
export function linkedOrderIdFor(linked: LinkedOrder | null | undefined, integrationKey: string): number | null {
  return linked && linked.vendorKey === integrationKey ? linked.id : null;
}
